import { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Badge, Button, Card, Form, Modal, ProgressBar, Spinner, Tab, Tabs } from 'react-bootstrap'
import { useLocation, useNavigate } from 'react-router-dom'
import useFetch from '../../../_fetch'

type SyncCredential = {
  id: string
  label: string
  initialized: boolean
  disabled: boolean
  error: string | null
}

type SyncNetwork = {
  name: string
  credentials: SyncCredential[]
}

type SyncUser = {
  username: string
  kind: 'publisher' | 'advertiser'
  networks: SyncNetwork[]
}

type SyncUserSummary = Pick<SyncUser, 'username' | 'kind'>

type SyncJob = {
  id: string
  username: string
  network: string
  credentialsId: string
  fromDate: string
  toDate: string
  status: 'running' | 'completed' | 'failed'
  syncTransactions: boolean
  syncClicks: boolean
  phase: string
  current: number
  total: number
  message: string | null
  startedAt: string
  updatedAt: string
  transactions: number
  clicks: number
  error: string | null
}

type DeleteTarget = {
  network: string
  credential: SyncCredential
}

function dateInputValue(date: Date) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-')
}

function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function pollInterval(network: string) {
  return network === 'Awin' ? 4000 : 10000
}

function elapsedTime(startedAt: string) {
  const seconds = Math.max(0, Math.floor((Date.now() - Date.parse(startedAt)) / 1000))
  const minutes = Math.floor(seconds / 60)
  return `${minutes}m ${seconds % 60}s`
}

function completedSummary(job: SyncJob) {
  const parts = []
  if(job.syncTransactions) parts.push(`${job.transactions} transactions`)
  if(job.syncClicks) parts.push(`${job.clicks} clicks`)
  return parts.join(' and ')
}

function CredentialStatus({ credential }: { credential: SyncCredential }) {
  const status = credential.disabled
    ? { label: 'Disabled', variant: 'secondary' }
    : credential.error
      ? { label: 'Error', variant: 'danger' }
      : credential.initialized
        ? { label: 'Active', variant: 'success' }
        : { label: 'Not initialized', variant: 'secondary' }

  return <Badge bg={status.variant}>{status.label}</Badge>
}

export default function NetworkCredentials() {
  const { doFetch } = useFetch()
  const location = useLocation()
  const navigate = useNavigate()
  const activeTab = location.pathname.endsWith('/data-sync') ? 'data-sync' : 'credentials'
  const today = useMemo(() => new Date(), [])
  const defaultFrom = useMemo(() => {
    const date = new Date(today)
    date.setDate(date.getDate() - 30)
    return dateInputValue(date)
  }, [today])

  const [users, setUsers] = useState<SyncUserSummary[]>([])
  const [selectedUser, setSelectedUser] = useState<SyncUser | null>(null)
  const [username, setUsername] = useState('')
  const [network, setNetwork] = useState('')
  const [credentialsId, setCredentialsId] = useState('')
  const [fromDate, setFromDate] = useState(defaultFrom)
  const [toDate, setToDate] = useState(dateInputValue(today))
  const [syncTransactions, setSyncTransactions] = useState(true)
  const [syncClicks, setSyncClicks] = useState(true)
  const [loading, setLoading] = useState(true)
  const [credentialsLoading, setCredentialsLoading] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [job, setJob] = useState<SyncJob | null>(null)
  const [activeJobs, setActiveJobs] = useState<SyncJob[]>([])
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [updatingCredentialId, setUpdatingCredentialId] = useState('')
  const credentialsRequest = useRef(0)

  const networks = selectedUser?.networks || []
  const selectedNetwork = networks.find((item) => item.name === network)
  const credentials = selectedNetwork?.credentials || []
  const selectedCredential = credentials.find((item) => item.id === credentialsId)
  const runningJobs = job?.status === 'running' && !activeJobs.some((item) => item.id === job.id)
    ? [job, ...activeJobs]
    : activeJobs
  const isCredentialSyncing = (networkName: string, credentialId: string) => runningJobs.some((item) =>
    item.username === username && item.network === networkName && item.credentialsId === credentialId)

  const loadUser = async (nextUsername: string, preferredNetwork = '', preferredCredentialsId = '') => {
    const request = ++credentialsRequest.current
    setCredentialsLoading(true)
    setSelectedUser(null)
    setNetwork('')
    setCredentialsId('')

    if(!nextUsername) {
      setCredentialsLoading(false)
      return
    }

    try {
      const res = await doFetch(`/user/admin/data-sync/users/${encodeURIComponent(nextUsername)}`)
      if(res.status >= 400) throw await res.text()
      const body = await res.json()
      if(request !== credentialsRequest.current) return

      const user: SyncUser = body.user
      const nextNetwork = user.networks.find((item) => item.name === preferredNetwork) || user.networks[0]
      const nextCredential = nextNetwork?.credentials.find((item) => item.id === preferredCredentialsId) || nextNetwork?.credentials[0]
      setSelectedUser(user)
      setNetwork(nextNetwork?.name || '')
      setCredentialsId(nextCredential?.id || '')
    } catch(err: any) {
      if(request === credentialsRequest.current) {
        setError(typeof err === 'string' ? err : 'Could not load network credentials')
      }
    } finally {
      if(request === credentialsRequest.current) setCredentialsLoading(false)
    }
  }

  const load = async () => {
    setLoading(true)
    setError('')
    try {
      const res = await doFetch('/user/admin/data-sync')
      if(res.status >= 400) throw await res.text()

      const body = await res.json()
      const nextUsers: SyncUserSummary[] = Array.isArray(body.users) ? body.users : []
      setUsers(nextUsers)
      setActiveJobs(Array.isArray(body.jobs) ? body.jobs.filter((item: SyncJob) => item.status === 'running') : [])

      const nextUser = nextUsers.find((item) => item.username === username) || nextUsers[0]
      setUsername(nextUser?.username || '')
      await loadUser(nextUser?.username || '', network, credentialsId)
    } catch(err: any) {
      setError(typeof err === 'string' ? err : 'Could not load data sync settings')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if(!activeJobs.length) return
    const timer = window.setTimeout(async () => {
      try {
        const nextJobs = await Promise.all(activeJobs.map(async (activeJob) => {
          const res = await doFetch(`/user/admin/data-sync/jobs/${activeJob.id}`)
          if(res.status >= 400) throw await res.text()
          return res.json()
        }))
        const running = nextJobs.filter((item) => item.status === 'running')
        setActiveJobs(running)
        if(running.length < nextJobs.length) await loadUser(username, network, credentialsId)
      } catch(err: any) {
        setError(typeof err === 'string' ? err : 'Could not update synchronization status')
      }
    }, activeJobs.some((activeJob) => activeJob.network === 'Awin') ? 4000 : 10000)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJobs])

  const selectUser = (nextUsername: string) => {
    setUsername(nextUsername)
    setJob(null)
    setError('')
    loadUser(nextUsername)
  }

  const selectNetwork = (nextNetworkName: string) => {
    const nextNetwork = networks.find((item) => item.name === nextNetworkName)
    setNetwork(nextNetworkName)
    setCredentialsId(nextNetwork?.credentials[0]?.id || '')
    setJob(null)
  }

  const startSync = async (event: React.FormEvent) => {
    event.preventDefault()
    setSyncing(true)
    setError('')
    setSuccess('')
    setJob(null)

    try {
      const res = await doFetch('/user/admin/data-sync', {
        username,
        network,
        credentialsId,
        fromDate: `${fromDate}T00:00:00.000Z`,
        toDate: `${toDate}T23:59:59.999Z`,
        syncTransactions,
        syncClicks,
      })
      if(res.status >= 400) throw await res.text()

      let currentJob: SyncJob = await res.json()
      setJob(currentJob)

      while(currentJob.status === 'running') {
        await wait(pollInterval(currentJob.network))
        const statusRes = await doFetch(`/user/admin/data-sync/jobs/${currentJob.id}`)
        if(statusRes.status >= 400) throw await statusRes.text()
        currentJob = await statusRes.json()
        setJob(currentJob)
      }

      await loadUser(username, network, credentialsId)
      if(currentJob.status === 'failed') setError(currentJob.error || 'Synchronization failed')
    } catch(err: any) {
      setError(typeof err === 'string' ? err : 'Could not synchronize data')
    } finally {
      setSyncing(false)
    }
  }

  const removeCredential = async () => {
    if(!deleteTarget) return
    setDeleting(true)
    setError('')
    setSuccess('')

    try {
      const res = await doFetch('/user/admin/data-sync/credentials', {
        username,
        network: deleteTarget.network,
        credentialsId: deleteTarget.credential.id,
      }, 'DELETE')
      if(res.status >= 400) throw await res.text()

      setSuccess(`${deleteTarget.credential.label} was deleted.`)
      setDeleteTarget(null)
      await loadUser(username, network, credentialsId)
    } catch(err: any) {
      setError(typeof err === 'string' ? err : 'Could not delete credentials')
    } finally {
      setDeleting(false)
    }
  }

  const toggleCredential = async (networkName: string, credential: SyncCredential) => {
    setUpdatingCredentialId(credential.id)
    setError('')
    setSuccess('')

    try {
      const disabled = !credential.disabled
      const res = await doFetch('/user/admin/data-sync/credentials', {
        username,
        network: networkName,
        credentialsId: credential.id,
        disabled,
      }, 'PATCH')
      if(res.status >= 400) throw await res.text()

      setSuccess(`${credential.label} was ${disabled ? 'disabled' : 'enabled'}.`)
      await loadUser(username, network, credentialsId)
    } catch(err: any) {
      setError(typeof err === 'string' ? err : 'Could not update credentials')
    } finally {
      setUpdatingCredentialId('')
    }
  }

  return (
    <div className="pe-2">
      <Card>
        <Card.Body>
          <h5 className="mb-1">Network credentials</h5>
          <div className="text-muted small mb-3">Manage credentials and synchronize network data for publishers and advertisers.</div>

          {loading ? (
            <div className="d-flex align-items-center gap-2 text-muted mb-3"><Spinner size="sm" /> Loading users…</div>
          ) : (
            <Form.Group style={{ maxWidth: 420 }} className="mb-3">
              <Form.Label>User</Form.Label>
              <Form.Select value={username} onChange={(event) => selectUser(event.target.value)}>
                {users.map((user) => (
                  <option key={user.username} value={user.username}>{user.username} ({user.kind})</option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
          {job?.status === 'completed' && (
            <Alert variant="success">
              Synchronization completed: {completedSummary(job)}.
            </Alert>
          )}
          {runningJobs.map((runningJob) => (
            <Alert variant="info" key={runningJob.id}>
              <div className="d-flex justify-content-between gap-3 flex-wrap">
                <strong>{runningJob.username} · {runningJob.network}</strong>
                <span>{elapsedTime(runningJob.startedAt)}</span>
              </div>
              <div className="small mt-1">
                {runningJob.phase || 'Synchronizing'}
                {runningJob.total > 0 && ` · accounts checked ${runningJob.current}/${runningJob.total}`}
                {' · '}{runningJob.fromDate.slice(0, 10)} – {runningJob.toDate.slice(0, 10)}
              </div>
              {runningJob.total > 0 && (
                <ProgressBar className="mt-2" now={runningJob.current} max={runningJob.total} />
              )}
              {runningJob.message && <div className="small mt-2">{runningJob.message}</div>}
              <div className="small text-muted mt-1">
                Last activity: {new Date(runningJob.updatedAt || runningJob.startedAt).toLocaleTimeString()}
              </div>
            </Alert>
          ))}
          <Tabs
            activeKey={activeTab}
            onSelect={(key) => key && navigate(`/network-credentials/${key}`)}
            className="mb-3"
          >
            <Tab eventKey="data-sync" title="Data sync">
              {!loading && (credentialsLoading ? (
                <div className="d-flex align-items-center gap-2 text-muted"><Spinner size="sm" /> Loading network credentials…</div>
              ) : !networks.length ? (
                <Alert variant="info">This user has no network credentials to synchronize.</Alert>
              ) : (
                <>
                  <Form onSubmit={startSync}>
                    <div className="d-flex flex-wrap gap-3">
                      <Form.Group style={{ minWidth: 220, flex: '1 1 220px' }}>
                        <Form.Label>Network</Form.Label>
                        <Form.Select value={network} onChange={(event) => selectNetwork(event.target.value)} required>
                          {networks.map((item) => <option key={item.name} value={item.name}>{item.name}</option>)}
                        </Form.Select>
                      </Form.Group>

                      {credentials.length > 1 && (
                        <Form.Group style={{ minWidth: 220, flex: '1 1 220px' }}>
                          <Form.Label>Credentials</Form.Label>
                          <Form.Select value={credentialsId} onChange={(event) => setCredentialsId(event.target.value)} required>
                            {credentials.map((item) => (
                              <option key={item.id} value={item.id}>{item.label}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      )}
                    </div>

                  {selectedCredential && (
                    <div className="mt-3">
                      <span className="me-3">Credential: <strong>{selectedCredential.label}</strong></span>
                      Status:{' '}
                      {isCredentialSyncing(network, selectedCredential.id)
                        ? <Badge bg="info">Synchronizing</Badge>
                        : <CredentialStatus credential={selectedCredential} />}
                      {selectedCredential.error && <pre className="small bg-light border rounded p-2 mt-2 mb-0">{selectedCredential.error}</pre>}
                    </div>
                  )}

                  <Form.Group className="mt-3">
                    <Form.Label>Data to synchronize</Form.Label>
                    <div className="d-flex gap-4">
                      <Form.Check
                        id="sync-transactions"
                        type="checkbox"
                        label="Transactions"
                        checked={syncTransactions}
                        onChange={(event) => setSyncTransactions(event.target.checked)}
                      />
                      <Form.Check
                        id="sync-clicks"
                        type="checkbox"
                        label="Clicks"
                        checked={syncClicks}
                        onChange={(event) => setSyncClicks(event.target.checked)}
                      />
                    </div>
                    {!syncTransactions && !syncClicks && <Form.Text className="text-danger">Select at least one data type.</Form.Text>}
                  </Form.Group>

                  <div className="d-flex flex-wrap gap-3 mt-3">
                    <Form.Group style={{ minWidth: 220, flex: '1 1 220px' }}>
                      <Form.Label>From</Form.Label>
                      <Form.Control type="date" value={fromDate} max={toDate} onChange={(event) => setFromDate(event.target.value)} required />
                    </Form.Group>
                    <Form.Group style={{ minWidth: 220, flex: '1 1 220px' }}>
                      <Form.Label>To</Form.Label>
                      <Form.Control type="date" value={toDate} min={fromDate} onChange={(event) => setToDate(event.target.value)} required />
                    </Form.Group>
                  </div>

                    <Button className="mt-3" type="submit" disabled={syncing || isCredentialSyncing(network, credentialsId) || (!syncTransactions && !syncClicks) || !username || !network || !credentialsId || fromDate > toDate}>
                      {syncing && <Spinner animation="border" size="sm" className="me-2" />}
                      {syncing ? 'Synchronizing…' : 'Synchronize data'}
                    </Button>
                  </Form>

                  <div className="border rounded bg-light p-3 mt-4 small text-muted">
                    <div className="fw-semibold text-body mb-1">Synchronization behavior</div>
                    Manual synchronization cannot start while hourly or daily synchronization is running. If a scheduled run starts during this job, it skips this user and network while continuing with all other data. On failure, the credentials remain disabled and the technical error is saved for admins.
                  </div>
                </>
              ))}
            </Tab>

            <Tab eventKey="credentials" title="Credentials">
              {!loading && (credentialsLoading ? (
                <div className="d-flex align-items-center gap-2 text-muted"><Spinner size="sm" /> Loading network credentials…</div>
              ) : (
                <>
                  <Alert variant="info">
                    Disabling credentials stops scheduled synchronization without deleting existing data.
                  </Alert>
                  {!networks.length && <Alert variant="info">This user has no network credentials.</Alert>}
                  {networks.map((item) => (
                    <div className="border rounded p-3 mb-3" key={item.name}>
                      <div className="fw-semibold mb-2">{item.name}</div>
                      {item.credentials.map((credential) => (
                        <div className="d-flex align-items-start justify-content-between gap-3 border-top py-3" key={credential.id}>
                          <div className="flex-grow-1" style={{ minWidth: 0 }}>
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              <span>{credential.label}</span>
                              {isCredentialSyncing(item.name, credential.id)
                                ? <Badge bg="info">Synchronizing</Badge>
                                : <CredentialStatus credential={credential} />}
                            </div>
                            {credential.error && <pre className="small bg-light border rounded p-2 mt-2 mb-0 text-wrap">{credential.error}</pre>}
                          </div>
                          <div className="d-flex gap-2">
                            <Button
                              size="sm"
                              variant={credential.disabled ? 'outline-success' : 'outline-secondary'}
                              disabled={deleting || syncing || !!updatingCredentialId}
                              onClick={() => toggleCredential(item.name, credential)}
                            >
                              {updatingCredentialId === credential.id && <Spinner animation="border" size="sm" className="me-2" />}
                              {credential.disabled ? 'Enable' : 'Disable'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              disabled={deleting || syncing || !!updatingCredentialId}
                              onClick={() => setDeleteTarget({ network: item.name, credential })}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </>
              ))}
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>

      <Modal show={!!deleteTarget} onHide={() => !deleting && setDeleteTarget(null)} centered>
        <Modal.Header closeButton={!deleting}><Modal.Title>Delete network credentials?</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="mb-2">Delete <strong>{deleteTarget?.credential.label}</strong> from <strong>{deleteTarget?.network}</strong> for <strong>{username}</strong>?</p>
          <Alert variant="danger" className="mb-0">
            The credentials and all associated transactions, clicks and publisher campaign data will be permanently removed.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" disabled={deleting} onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="danger" disabled={deleting} onClick={removeCredential}>
            {deleting && <Spinner animation="border" size="sm" className="me-2" />}
            Delete credentials
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
